import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Check, Sparkles, BookOpen, Repeat, Camera, PartyPopper, Hammer, Mic, RefreshCw } from "lucide-react";

const STEPS = [
  { icon: Sparkles, tag: "Bienvenue", title: "Bienvenue sur KLT Lead Capture ⚡", desc: "L'outil de précision des commerciaux : capturez un lead et faites votre débrief en quelques secondes. Ce tutoriel dure 1 minute.", color: "from-[#3B82F6] to-[#1D4ED8]" },
  { icon: Mic, tag: "Étape 1", title: "Débrief vocal IA", desc: "Appuyez sur le gros bouton IA et parlez 30 secondes. Whisper transcrit, GPT résume, calcule la probabilité de signature et range le débrief dans la bonne fiche prospect — ou en crée une nouvelle.", color: "from-blue-500 to-indigo-600" },
  { icon: Camera, tag: "Étape 2 · Capture éclair", title: "Carte de visite, NFC ou QR", desc: "Scannez une carte papier (l'IA remplit la fiche), lisez une carte NFC ou faites scanner votre QR code. Après chaque capture : fixez un RDV ou envoyez un SMS de remerciement IA.", color: "from-sky-500 to-blue-600" },
  { icon: PartyPopper, tag: "Étape 3 · Priorisation", title: "Probabilité de signature", desc: "Vos leads sont triés automatiquement : 🔥 Chaud, 🟡 Moyen, ⚪ Froid. Un appui sur 🎙️ pour relancer ou débriefer un prospect.", color: "from-amber-500 to-orange-500" },
  { icon: RefreshCw, tag: "Étape 4 · CRM", title: "Synchronisation CRM", desc: "Envoyez vos fiches vers HubSpot, Salesforce ou Pipedrive en un clic, avec les relances e-mail automatiques (J+1, J+3, J+7).", color: "from-cyan-500 to-blue-600" },
  { icon: Hammer, tag: "Étape 5 · Gestion", title: "Mode Gestion inclus", desc: "Besoin d'aller plus loin ? La Trésorerie, les Devis signables et les Projets avec planning sont accessibles gratuitement via le Mode Gestion.", color: "from-[#3B82F6] to-violet-600" },
];

const GLOSSARY = [
  { term: "Lead / Prospect", def: "Une personne rencontrée qui pourrait devenir client." },
  { term: "Mode Salon / Atelier", def: "Salon = prospection sans chiffres affichés. Atelier = gestion des projets, du planning et de l'argent." },
  { term: "Métré", def: "Le rendez-vous sur place pour prendre les mesures avant de faire le devis." },
    { term: "CA encaissé / prévisionnel", def: "Encaissé = l'argent réellement reçu. Prévisionnel = ce qui doit encore rentrer, aux dates prévues." },
  { term: "OCR", def: "La technologie qui « lit » une carte de visite prise en photo." },
  { term: "Température", def: "Le niveau d'intérêt d'un prospect : 🔥 chaud, 🌤️ tiède, ❄️ froid." },
  { term: "CRM", def: "Le logiciel où votre entreprise gère ses clients (HubSpot, Salesforce…)." },
];

export const WelcomeOnboarding = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const total = STEPS.length + 1;
  const isGlossary = step === STEPS.length;
  const last = step === total - 1;
  const S = STEPS[step];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="welcome-onboarding" className="max-w-md rounded-3xl p-0 overflow-hidden gap-0">
        {!isGlossary ? (
          <div className="bg-gradient-to-br from-[#A855F7]/10 via-[#EC4899]/5 to-transparent px-6 pt-7 pb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A855F7] flex items-center gap-1.5" data-testid="onboarding-tag"><Sparkles className="w-3.5 h-3.5" /> {S.tag}</p>
            <div className={`mt-5 w-16 h-16 rounded-2xl bg-gradient-to-br ${S.color} flex items-center justify-center shadow-lg klt-fade-up`} key={step}>
              <S.icon className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-4" data-testid="onboarding-title">{S.title}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{S.desc}</p>
          </div>
        ) : (
          <div className="px-6 pt-7 pb-3 bg-gradient-to-br from-[#A855F7]/10 to-transparent">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A855F7] flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Petit lexique</p>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-3">Les mots à connaître</h2>
            <div className="mt-4 max-h-[46vh] overflow-y-auto pr-1 space-y-3" data-testid="glossary-list">
              {GLOSSARY.map((g) => (
                <div key={g.term} className="rounded-2xl bg-secondary/60 p-3">
                  <p className="text-sm font-bold">{g.term}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{g.def}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="px-6 pb-6 pt-4 flex items-center justify-between border-t border-border/60">
          <div className="flex gap-1.5" data-testid="onboarding-dots">
            {Array.from({ length: total }).map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#EC4899]" : "w-1.5 bg-border"}`} />)}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && <Button variant="ghost" onClick={() => setStep((p) => p - 1)} data-testid="onboarding-prev" className="rounded-xl text-muted-foreground px-3"><ArrowLeft className="w-4 h-4" /></Button>}
            {!last && <Button variant="ghost" onClick={onClose} data-testid="onboarding-skip" className="rounded-xl text-muted-foreground">Passer</Button>}
            <Button onClick={() => (last ? onClose() : setStep((p) => p + 1))} data-testid="onboarding-next" className="rounded-xl font-semibold">
              {last ? (<><Check className="w-4 h-4 mr-2" /> C'est parti</>) : (<>Suivant <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeOnboarding;
